import { useQuery } from '@tanstack/react-query';
import { Box, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import { Loader2, Newspaper, Waves } from 'lucide-react';

import { getRecentCatalystEvents } from '../api/client';
import { CatalystEventsList } from './CatalystEventsList';

export function SignalsView() {
  const catalystsQuery = useQuery({
    queryKey: ['signals', 'recent'],
    queryFn: ({ signal }) => getRecentCatalystEvents(100, { signal }),
    refetchInterval: 10_000,
  });
  const items = catalystsQuery.data?.items ?? [];

  return (
    <Stack as="section" mt="8" gap="5">
      <Flex flexDir={{ base: 'column', xl: 'row' }} gap="3" align={{ xl: 'flex-end' }} justify={{ xl: 'space-between' }}>
        <Stack gap="3">
          <Flex align="center" gap="3">
            <Box color="signal.blue">
              <Newspaper size={28} />
            </Box>
            <Heading as="h1" fontSize="3xl" fontWeight="semibold" letterSpacing="tight">
              Signals
            </Heading>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Recent catalyst events emitted by the backend.
          </Text>
        </Stack>
        {catalystsQuery.isFetching ? (
          <Box
            display="inline-flex"
            alignItems="center"
            gap="2"
            borderRadius="oqPanel"
            px="4"
            py="2.5"
            layerStyle="glassPanel"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <Loader2 className="oq-spin" size={16} />
            </Box>
            <Text as="span" fontSize="sm" color="text.muted">
              Refreshing
            </Text>
          </Box>
        ) : null}
      </Flex>

      <Box as="section" mt="5" overflow="hidden" borderRadius="oqPanel" layerStyle="glassPanel">
        {catalystsQuery.isError ? (
          <SignalsEmptyState
            title="Signals unavailable."
            detail="The backend did not return recent catalyst events."
          />
        ) : items.length === 0 ? (
          <SignalsEmptyState
            title="No signals yet."
            detail="Oddsquake is watching catalyst events from market clusters, timing boundaries, liquidity warnings, and external news enrichment."
          />
        ) : (
          <CatalystEventsList items={items} mode="full" />
        )}
      </Box>
    </Stack>
  );
}

function SignalsEmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box display="grid" minH="360px" placeItems="center" px="5" py="10">
      <Box maxW="md" textAlign="center">
        <Box
          mx="auto"
          display="grid"
          h="14"
          w="14"
          placeItems="center"
          borderRadius="oqPanel"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.200"
          color="signal.blue"
        >
          <Waves size={28} />
        </Box>
        <Heading as="h3" mt="5" fontSize="xl" fontWeight="semibold" color="text.primary">
          {title}
        </Heading>
        <Text mt="2" fontSize="sm" lineHeight="6" color="text.muted">
          {detail}
        </Text>
      </Box>
    </Box>
  );
}